import { useEffect, useMemo, useState } from 'react';
import { StyleSheet, Text, View } from 'react-native';
import { useTranslation } from 'react-i18next';
import { DEFAULT_HANDHELD_STEP, HANDHELD_STEP_OPTIONS } from '@heraion/shared';

import { Chip } from '../primitives/Chip';
import type { useTheme } from './theme';
import { formatUnitValue, formatUnitWeight, weightLocale } from './plateCalcMath';
import { unitWord, weightUnitLabels } from '../weightUnitLabels';
import type { makeStyles } from './plateCalcModalStyles';
import type { WeightUnit } from './state/loggerDraft';
import { loadHandheldStep, persistHandheldStep } from './state/plateConfig';

// #49: the step of the athlete's fixed-dumbbell rack: «i manubri fissi vanno
// di 1 o di 2». Shown in the plate calculator for a dumbbell exercise only,
// where the bar and the plates that sit beside it do not apply.
//
// ⛔ Device-local, like the bar and the inventory (`plateConfig`): nothing here
// goes to the profile, the API or the wire. Un-chosen reads as 2, which is
// loadable on a rack that goes by 1; 2.5 is not.
//
// State lives HERE and not in the modal's hook: it feeds no calculation of this
// modal (a dumbbell has no plates), so it is not a second home for a shared
// input. The rounding family reads it back from `loadRoundingContext`.
//
// ⚠️ The unit is said once, in the label, and the chips are bare, like the bar
// row. The spoken label keeps the unit: a reader that lands on a chip mid-row
// does not get the group title again.

type Props = {
  theme: ReturnType<typeof useTheme>;
  styles: ReturnType<typeof makeStyles>;
  unit: WeightUnit;
};

export function DumbbellStepSection({ theme, styles, unit }: Props) {
  const { t, i18n } = useTranslation();
  const locale = weightLocale(i18n);
  const unitLabels = weightUnitLabels(t);
  const [step, setStep] = useState<number>(DEFAULT_HANDHELD_STEP);

  // Re-read when the unit turns: kg and lb are two racks, each with its own step.
  useEffect(() => {
    let cancelled = false;
    void loadHandheldStep(unit).then((v) => {
      if (!cancelled) setStep(v);
    });
    return () => {
      cancelled = true;
    };
  }, [unit]);

  const local = useMemo(
    () =>
      StyleSheet.create({
        // sm, not xs: the hint is a sentence the athlete has to read.
        hint: { color: theme.palette.ink3, fontSize: theme.typography.sizes.sm },
      }),
    [theme],
  );

  const label = t('mobile.plates.dumbbellStepLabel', { unit: unitWord(t, unit) });
  const select = (value: number) => {
    setStep(value);
    void persistHandheldStep(unit, value);
  };

  return (
    <View style={styles.configCard}>
      <Text style={styles.sectionLabel}>{label}</Text>
      <View
        accessibilityRole="radiogroup"
        accessibilityLabel={t('mobile.plates.dumbbellStepAria', { unit: unitWord(t, unit) })}
        style={styles.chipRow}
      >
        {HANDHELD_STEP_OPTIONS[unit].map((value) => (
          <Chip
            key={value}
            theme={theme}
            role="radio"
            shape="box"
            label={formatUnitValue(value, unit, locale)}
            accessibilityLabel={t('mobile.plates.dumbbellStepOption', {
              weight: formatUnitWeight(value, unit, locale, unitLabels),
            })}
            selected={step === value}
            onPress={() => select(value)}
          />
        ))}
      </View>
      <Text style={local.hint}>{t('mobile.plates.dumbbellStepHint')}</Text>
    </View>
  );
}
